import { computed, ref, shallowRef, watch } from '@vue/composition-api';
import { useStorage } from '@vueuse/core';
import Vue from 'vue';
import { designPath } from '@/config/constant';
import wujiFetch from '@/utils/wujiFetch';
import getXyManagePathPrefix from '@/utils/getXyManagePathPrefix';
const KNOT_BASE_URL = `${designPath}/knot`;
const GOGO_SWITCH_KEY = 'wuji:gogo:open';
// 是否在部署配置中开启功能
export const getDeployOpen = () => window?.GLOBAL_INFO?.wujiGoConfig?.enable || false;
/**
 * 运维级 platform123（"123 平台"）发布功能总开关。
 * 对应 server 端 `xySetting.gogoBackend.config123.enable`，由 Http.js 下发、html.js 注入到 window.GLOBAL_INFO.platform123Enable。
 * 未启用时，所有 123 相关 UI（发版页部署方式选择、发版设置页 123 配置、后台服务页）都不渲染；
 * server 端 `isPlatform123Enabled` 也内置了同一个判断，Hook 会走原生链路。
 */
export const getPlatform123GlobalEnable = () => !!window?.GLOBAL_INFO?.platform123Enable;
// 是否开启了 wuji gogo 后端构建能力
const gogoBackendEnabled = Vue.observable({ value: false });
const gogoRedisEnabled = Vue.observable({ value: false });
export const gogoBackendAllocateResult = shallowRef(null);
export const getGogoBackendEnabled = () => gogoBackendEnabled.value;
export const getGogoRedisEnabled = () => gogoRedisEnabled.value;
export const fetchGogoBackendEnabled = async () => {
    const query = new URLSearchParams(window.location.search);
    const projectId = query.get('projectid') || query.get('projectId');
    // 获取项目是否支持构建后端
    await (async () => {
        const resp = await wujiFetch(`${getXyManagePathPrefix({ envId: 'dev' })}/ai-project/backend/allocate?projectId=${projectId}`);
        gogoBackendAllocateResult.value = resp;
        return !!resp;
    })().catch(() => false)
        .then(v => gogoBackendEnabled.value = (!!v));
};
/** 检测连接的 server 是否支持 knot 技能发布（按 skill 粒度）。 */
const knotSkillPublishEnabled = Vue.observable({ value: false });
export const getKnotSkillPublishEnabled = () => knotSkillPublishEnabled.value;
export const fetchKnotSkillPublishEnabled = async (projectId) => {
    await (async () => {
        const resp = await wujiFetch(`${KNOT_BASE_URL}/capability?projectid=${encodeURIComponent(projectId)}`);
        return !!resp?.knotSkillPublish;
    })().catch(() => false)
        .then(v => knotSkillPublishEnabled.value = v);
};
/** 根据平台 Redis Proxy 健康状态决定是否启用 wuji-redis skill。 */
export const fetchGogoRedisEnabled = async () => {
    const query = new URLSearchParams(window.location.search);
    const projectId = query.get('projectid') || query.get('projectId');
    await (async () => {
        const resp = await wujiFetch(`${getXyManagePathPrefix({ envId: 'dev' })}/ai-project/backend/redis-status?projectId=${projectId}`);
        return !!resp?.enabled;
    })().catch(() => false)
        .then(v => gogoRedisEnabled.value = (!!v));
};
// 是否在 Query 参数中开启测试模式
export const getSearchDevOpen = () => new URLSearchParams(window.location.search).has('gogoDev');
// 获取测试版本地址
export const getTestBaseUrl = () => window.GOGO_EDITOR_BASE_URL || new URLSearchParams(window.location.search).get('testBaseUrl');
const isLocalGogoOpen = useStorage(GOGO_SWITCH_KEY, false);
export function useGogo() {
    const isDeployOpen = ref(getDeployOpen());
    const isSearchDevOpen = ref(getSearchDevOpen());
    const isOpen = computed(() => isDeployOpen.value || isSearchDevOpen.value || isLocalGogoOpen.value);
    // 如果 Query 中开启过测试模式，则本地存储为开启状态
    watch(isSearchDevOpen, (val) => {
        if (val)
            isLocalGogoOpen.value = true;
    }, { immediate: true });
    return {
        isOpen,
    };
}
